import JSZip from 'jszip';
import { safeAxios } from '@/services/safeAxios';
import { API_ROUTES } from '@/api/apiRoutes';
import { publicHttpClient } from '@/services/publicHttpClient';
import { HandleType } from '@/enums/handle-type.enum';
import { log } from '@/logger/logger.ts';
import { ModelType } from '@/enums/model-type.enum';
import { runModel } from '@/components/Nodes/RunModel';
import { useWorkflowStore } from '@/state/workflow.state';
import { assetUrl } from '@/utils/assetUrls';
import { FlowGraph } from '../FlowGraph';
import type { Input, MaskExtractorState, NodeId } from 'web';
import type { MaskExtractorData } from '@/types/node';

const logger = log.getLogger('MaskExtractorView');

const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const inputId = 'image';
export class MaskExtractorView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  private getData(): MaskExtractorData {
    return this.graph.store.getNodeData<MaskExtractorData>(this.nodeId);
  }

  setState(state: MaskExtractorState) {
    if (!this.graph.store.getNodeNullable(this.nodeId)) return;
    this.graph.wasmChange((wasm) => wasm.setMaskExtractorState(this.nodeId, state));
  }

  getState(): MaskExtractorState {
    return this.getData().state;
  }

  hasInput(): boolean {
    const inputType = this.graph.nodeInputType(this.nodeId, inputId);
    return inputType === HandleType.Image || inputType === HandleType.Mask;
  }

  toggleDisplayMode() {
    this.graph.wasmEdit((wasm) => {
      return wasm.setMaskExtractorDisplayMode(this.nodeId, this.getData().displayMode === 'image' ? 'mask' : 'image');
    });
  }

  // NOTE: relies on a useEffect to initiate a state transition
  recalculateMask() {
    this.setState({ type: 'empty' });
  }

  private async runAnalyseMasks(
    runVisualId: string,
    recipeId: string,
    recipeVersion: number,
    onError: (e: any) => void,
  ) {
    return new Promise<void>((resolve, reject) => {
      void runModel(
        { input: ['visualId'] },
        { visualId: runVisualId },
        { name: ModelType.BRMasks, type: ModelType.BRMasks },
        {},
        this.nodeId,
        recipeId,
        recipeVersion,
        {
          onSuccess: (results: unknown, _remainingCredits) => {
            if (
              results &&
              typeof results === 'object' &&
              'objects_masks' in results &&
              results.objects_masks &&
              typeof results.objects_masks === 'string'
            ) {
              void this.downloadAndExtractZip(results.objects_masks, runVisualId).then(resolve);
            }
          },
          onError: (e: Error) => {
            onError(e);
            reject(e);
          },
        },
        this.getData().version,
      );
    });
  }

  private async downloadAndExtractZip(zipUrl: string, visualId: string) {
    try {
      const response = await publicHttpClient.get(zipUrl, { responseType: 'arraybuffer' });
      const zip = await JSZip.loadAsync(response.data);
      const extractedFiles: [string, string][] = [];
      let panopticImg = '';

      for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
        if (!zipEntry.dir) {
          const content = await zipEntry.async('blob');
          const dataUrl = await blobToDataURL(content);
          if (relativePath.includes('panoptic')) {
            panopticImg = dataUrl;
          } else {
            extractedFiles.push([relativePath, dataUrl]);
          }
        }
      }

      this.setState({
        type: 'done',
        visualId,
        panopticImage: panopticImg,
        selectedMasks: [],
        masks: parseMaskFiles(extractedFiles),
      });
      this.graph.wasm.call((wasm) => {
        wasm.loadMaskResources();
      });
    } catch (error) {
      throw new Error('Error downloading or extracting zip file', { cause: error });
    }
  }

  private async registerImageAndAnalyseMasks(recipeId: string, recipeVersion: number, onError: (e: any) => void) {
    //// 1. get image url for upload
    const evaluatedInput = this.graph.evaluateInput(this.nodeId, inputId);
    if (!evaluatedInput || evaluatedInput.type !== 'image') return;

    let imageUrl: string | undefined;
    if (evaluatedInput.value.type === 'computed') {
      const input = this.getData().image;
      if (!input) return;
      const img = this.graph.wasm.call((wasm) => wasm.exportNodeRender(input.nodeId, input.outputId));
      imageUrl = img?.data;
    } else {
      imageUrl = assetUrl(evaluatedInput.value, useWorkflowStore.getState().resolvedAssets);
    }
    if (!imageUrl) return;

    //// 2. register image with Bria to get a visualId
    let visualId: string | undefined;
    try {
      const res = await safeAxios.post<{ visualId: string }>(API_ROUTES.models.imageRegister(), { url: imageUrl });
      visualId = res.data.visualId;
    } catch (error) {
      logger.error('Error registering image.', error);
      throw error;
    }
    if (!visualId) return;
    // advance state
    this.setState({ type: 'image_registered', visualId });

    //// 3. run the segmentation model
    await this.runAnalyseMasks(visualId, recipeId, recipeVersion, onError);
  }

  async handleStateTransition(
    prevInput: Input | undefined,
    recipeId: string,
    recipeVersion: number,
    onError: (e: any) => void,
  ) {
    const input = this.getData().image;

    // set state to empty if the input has changed
    if (input && prevInput && input.nodeId === prevInput.nodeId && input.outputId === prevInput.outputId) {
      const evaluatedInput = this.graph.evaluateInput(this.nodeId, inputId);
      const prevEvaluatedInput = this.graph.evaluateOutput(prevInput);

      // Only check evaluated inputs if we have a previous baseline
      if (prevEvaluatedInput && evaluatedInput) {
        // Both defined - compare dimensions
        if (
          prevEvaluatedInput.type === 'image' &&
          evaluatedInput.type === 'image' &&
          (evaluatedInput.value.width !== prevEvaluatedInput.value.width ||
            evaluatedInput.value.height !== prevEvaluatedInput.value.height)
        ) {
          // input dimensions have changed
          this.setState({ type: 'empty' });
        }
      } else if (prevEvaluatedInput && !evaluatedInput) {
        // Was valid before, now empty - upstream became empty
        this.setState({ type: 'empty' });
      }
      // If prevEvaluatedInput is undefined, we're still initializing - skip the check
    } else if (input !== prevInput) {
      // input has changed
      this.setState({ type: 'empty' });
    }

    // Only fetch visualId when we have an image input and state is empty
    if (this.hasInput() && this.getState().type === 'empty') {
      await this.registerImageAndAnalyseMasks(recipeId, recipeVersion, onError);
    }
  }
}

function parseMaskFiles(filePairs: [string, string][]): string[] {
  const maskPairs = filePairs
    .map(([key, url]) => {
      const maskIndex = getMaskIndex(key);
      return maskIndex !== undefined ? ([maskIndex, url] as const) : undefined;
    })
    .filter((pair) => pair != undefined);

  const maxIndex = Math.max(...maskPairs.map(([k]) => k));
  const masks: string[] = Array.from({ length: maxIndex + 1 }, () => '');
  for (const [index, value] of maskPairs) {
    masks[index] = value;
  }

  return masks;
}

function getMaskIndex(key: string): number | undefined {
  const maskIndexStr = key.match(/_(\d+).png$/)?.[1];
  if (maskIndexStr === undefined) return undefined;
  const maskIndex = Number(maskIndexStr);
  if (!Number.isFinite(maskIndex)) return undefined;
  return maskIndex;
}
